import React, { useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  SafetyOutlined,
  QrcodeOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import logoShort from "../assets/logoShort.png";
import "./ModernLogin.css";
import { getPostLoginPath } from "../utils/roleUtils";

const TwoFactorLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Get email, sessionId, availableMethods from navigation state
  const email = location.state?.email || "";
  const sessionId = location.state?.sessionId || "";
  const availableMethods = location.state?.availableMethods || [];

  const baseUrl = process.env.REACT_APP_AUTH_URL || "http://localhost:8080/api";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!code) {
      setError("Please enter your 2FA code.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiClient.post(`${baseUrl}/2fa/login`, {
        email,
        code,
        sessionId,
      });
      // Handle new API response structure with nested data
      const responseData = response.data.success && response.data.data ? response.data.data : response.data;
      
      // On success, backend sets cookies; just redirect
      if (response.data.success) {
        toast.success("2FA verification successful!", {
          position: "top-right",
          autoClose: 3000,
          theme: isDark ? "dark" : "light",
        });
        
        const roles = responseData?.roles || [];
        navigate(getPostLoginPath(roles));
      } else {
        // Handle API failure response
        const errorMessage = response.data.error || response.data.message || "Invalid response from server.";
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: isDark ? "dark" : "light",
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid code or server error.");
      toast.error(
        err.response?.data?.message || "Invalid code or server error.",
        {
          position: "top-right",
          autoClose: 5000,
          theme: isDark ? "dark" : "light",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`modern-login-container${isDark ? " dark" : ""}`}
      style={{ minHeight: "100vh", display: "flex" }}
    >
      {/* Left Side - Security Illustration/Info */}
      <div
        className="login-hero"
        style={{
          flex: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "2rem",
        }}
      >
        <img
          src={logoShort}
          alt="SimplyBond"
          style={{ width: 60, marginBottom: 24 }}
        />
        <h1
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 800,
            fontSize: 32,
            marginBottom: 12,
          }}
        >
          Two-Factor Authentication
        </h1>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            opacity: 0.95,
            marginBottom: 24,
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          For your security, please enter the verification code from your
          authenticator app to continue.
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            alignItems: "flex-start",
            width: "100%",
            maxWidth: 350,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SafetyOutlined style={{ fontSize: 22 }} />
            <span>Enhanced Account Protection</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <QrcodeOutlined style={{ fontSize: 22 }} />
            <span>Works with Google Authenticator</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <LockOutlined style={{ fontSize: 22 }} />
            <span>Required for secure login</span>
          </div>
        </div>
        {availableMethods.length > 0 && (
          <div style={{ marginTop: 32, textAlign: "center" }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Available Methods:
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              {availableMethods.map((m) => (
                <span
                  key={m.methodCode}
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: 8,
                    padding: "4px 12px",
                    fontSize: 14,
                  }}
                >
                  {m.methodName}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Right Side - 2FA Form */}
      <div
        className="login-form-section"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDark ? "#181c2a" : "#fff",
        }}
      >
        <form
          className="login-form"
          onSubmit={handleSubmit}
          style={{
            width: "100%",
            maxWidth: 400,
            background: isDark ? "#23263a" : "#f7f8fa",
            borderRadius: 16,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            padding: 32,
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          <h2
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: 24,
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            Enter 2FA Code
          </h2>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 15 }}>Email</label>
            <input
              type="email"
              value={email}
              disabled
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: "1px solid #ccc",
                background: "#f0f0f0",
                marginTop: 4,
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 15 }}>
              Verification Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter 6-digit code"
              maxLength={6}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: error ? "1.5px solid #e74c3c" : "1px solid #ccc",
                marginTop: 4,
                fontFamily: "Inter, sans-serif",
                fontSize: 16,
                letterSpacing: 2,
                textAlign: "center",
              }}
              autoFocus
            />
            {error && (
              <div style={{ color: "#e74c3c", fontSize: 13, marginTop: 4 }}>
                {error}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading || !code}
            style={{
              width: "100%",
              padding: "12px 0",
              borderRadius: 8,
              background: isLoading ? "#b3b3b3" : "#667eea",
              color: "#fff",
              fontWeight: 700,
              fontSize: 17,
              border: "none",
              cursor: isLoading ? "not-allowed" : "pointer",
              marginTop: 8,
              transition: "background 0.2s",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {isLoading ? "Verifying..." : "Verify & Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorLogin;
